import * as React from "react";
import {
  ArrowClockwiseIcon,
  AtIcon,
  BellIcon,
  BellSimpleSlashIcon,
  CaretDownIcon,
  ChatCircleTextIcon,
  CheckCircleIcon,
  ChecksIcon,
  FileTextIcon,
  FolderSimpleIcon,
  ListBulletsIcon,
  PresentationChartIcon,
  SparkleIcon,
  UserPlusIcon,
  WarningCircleIcon,
  WarningIcon,
  type Icon
} from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";

import { Button } from "@libs/zapi-ui/ds/components/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@libs/zapi-ui/ds/components/collapsible";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@libs/zapi-ui/ds/components/empty";
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@libs/zapi-ui/ds/components/item";
import { LeoGlyph } from "@libs/zapi-ui/ds/components/leo-wordmark";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/ds/components/popover";
import { ScrollArea } from "@libs/zapi-ui/ds/components/scroll-area";
import { Skeleton } from "@libs/zapi-ui/ds/components/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/ds/components/tooltip";

type AppNotificationKind =
  | "comment"
  | "mention"
  | "invite"
  | "report"
  | "chapter-ready"
  | "leo-reply"
  | "outline-ready"
  | "slides-ready"
  | "outline-outdated"
  | "project-ready";

type AppNotificationGroup = {
  id: string;
  label: string;
};

type AppNotification = {
  id: string;
  kind: AppNotificationKind;
  title: string;
  description?: string;
  time: string;
  unread: boolean;
  group?: AppNotificationGroup;
};

type NotificationTone = "neutral" | "leo" | "ready" | "attention";

const kinds: Record<
  AppNotificationKind,
  { icon?: Icon; weight: "regular" | "duotone" | "bold"; tone: NotificationTone }
> = {
  comment: { icon: ChatCircleTextIcon, weight: "regular", tone: "neutral" },
  mention: { icon: AtIcon, weight: "regular", tone: "neutral" },
  invite: { icon: UserPlusIcon, weight: "regular", tone: "neutral" },
  "leo-reply": { weight: "regular", tone: "leo" },
  report: { icon: SparkleIcon, weight: "duotone", tone: "ready" },
  "chapter-ready": { icon: FileTextIcon, weight: "duotone", tone: "ready" },
  "outline-ready": { icon: ListBulletsIcon, weight: "duotone", tone: "ready" },
  "slides-ready": { icon: PresentationChartIcon, weight: "duotone", tone: "ready" },
  "project-ready": { icon: CheckCircleIcon, weight: "duotone", tone: "ready" },
  "outline-outdated": { icon: WarningIcon, weight: "bold", tone: "attention" }
};

const toneClass: Record<NotificationTone, string> = {
  neutral: "bg-muted text-muted-foreground",
  leo: "bg-card text-leo ring-1 ring-foreground/10",
  ready: "bg-success/10 text-success dark:bg-success/20",
  attention: "bg-primary-100 text-primary-text dark:bg-primary-950"
};

type NotificationsLabels = {
  title: string;
  trigger: (unread: number) => string;
  markAllRead: string;
  markRead: string;
  markGroupRead: (group: string) => string;
  groupSummary: (count: number, unread: number) => string;
  unread: string;
  loading: string;
  errorTitle: string;
  errorDescription: string;
  retry: string;
  emptyTitle: string;
  emptyDescription: string;
};

type NotificationsPanelProps = {
  notifications: AppNotification[];
  labels: NotificationsLabels;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  onMarkAllRead?: () => void;
  onMarkGroupRead?: (group: AppNotificationGroup) => void;
  onSelect?: (notification: AppNotification) => void;
};

type NotificationEntry =
  | { type: "notification"; notification: AppNotification }
  | { type: "group"; group: AppNotificationGroup; notifications: AppNotification[] };

function toEntries(notifications: AppNotification[]): NotificationEntry[] {
  const entries: NotificationEntry[] = [];
  const groups = new Map<string, Extract<NotificationEntry, { type: "group" }>>();
  for (const notification of notifications) {
    if (!notification.group) {
      entries.push({ type: "notification", notification });
      continue;
    }
    const existing = groups.get(notification.group.id);
    if (existing) {
      existing.notifications.push(notification);
      continue;
    }
    const entry = { type: "group" as const, group: notification.group, notifications: [notification] };
    groups.set(notification.group.id, entry);
    entries.push(entry);
  }
  return entries;
}

const countUnread = (notifications: AppNotification[]) =>
  notifications.filter((notification) => notification.unread).length;

function NotificationKindIcon({ kind, unread }: { kind: AppNotificationKind; unread: boolean }) {
  const { icon: KindIcon, weight, tone } = kinds[kind];
  return (
    <ItemMedia
      variant="icon"
      data-tone={tone}
      className={cn("size-8 rounded-full", unread ? toneClass[tone] : toneClass.neutral)}
    >
      {KindIcon ? <KindIcon weight={weight} /> : <LeoGlyph title="" className="size-4" />}
    </ItemMedia>
  );
}

function NotificationRow({
  notification,
  unreadLabel,
  onSelect
}: {
  notification: AppNotification;
  unreadLabel: string;
  onSelect?: (notification: AppNotification) => void;
}) {
  return (
    <Item
      size="sm"
      data-unread={notification.unread || undefined}
      render={<button type="button" onClick={() => onSelect?.(notification)} />}
      className="text-start hover:bg-accent"
    >
      <NotificationKindIcon kind={notification.kind} unread={notification.unread} />
      <ItemContent className="min-w-0 gap-0.5">
        <ItemTitle className={cn("line-clamp-2 typo-label-md", !notification.unread && "text-muted-foreground")}>
          {notification.title}
        </ItemTitle>
        {notification.description && (
          <ItemDescription className="line-clamp-1 typo-caption">{notification.description}</ItemDescription>
        )}
        <span className="typo-caption text-muted-foreground">{notification.time}</span>
      </ItemContent>
      {notification.unread && (
        <span className="size-2 shrink-0 self-start rounded-full bg-primary">
          <span className="sr-only">{unreadLabel}</span>
        </span>
      )}
    </Item>
  );
}

function NotificationGroup({
  group,
  notifications,
  labels,
  open,
  onOpenChange,
  onMarkRead,
  onSelect
}: {
  group: AppNotificationGroup;
  notifications: AppNotification[];
  labels: NotificationsLabels;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMarkRead?: (group: AppNotificationGroup) => void;
  onSelect?: (notification: AppNotification) => void;
}) {
  const unread = countUnread(notifications);
  return (
    <Collapsible data-slot="notification-group" open={open} onOpenChange={onOpenChange}>
      <div className="flex items-center gap-1 rounded-md pe-1 hover:bg-accent">
        <CollapsibleTrigger className="group/trigger flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-3 py-2.5 text-start outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
          <span className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <FolderSimpleIcon className="size-4" />
            {unread > 0 && (
              <span
                aria-hidden="true"
                className="absolute -end-0.5 -top-0.5 size-2 rounded-full bg-primary ring-2 ring-popover"
              />
            )}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className={cn("truncate typo-label-md", unread === 0 && "text-muted-foreground")}>{group.label}</span>
            <span className="typo-caption text-muted-foreground">
              {labels.groupSummary(notifications.length, unread)}
            </span>
          </span>
          <CaretDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[panel-open]/trigger:rotate-180" />
        </CollapsibleTrigger>
        {unread > 0 && onMarkRead && (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  shape="pill"
                  aria-label={labels.markGroupRead(group.label)}
                  onClick={() => onMarkRead(group)}
                />
              }
            >
              <ChecksIcon />
            </TooltipTrigger>
            <TooltipContent>{labels.markRead}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <CollapsibleContent>
        <div className="ms-7 flex flex-col border-s ps-1">
          {notifications.map((notification) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              unreadLabel={labels.unread}
              onSelect={onSelect}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function NotificationsSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="flex flex-col p-1">
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="flex items-start gap-2.5 px-3 py-2.5">
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5 pt-1">
            <Skeleton className={cn("h-3.5", index % 2 === 0 ? "w-3/4" : "w-1/2")} />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

function NotificationsPanel({
  notifications,
  labels,
  loading = false,
  error = false,
  onRetry,
  onMarkAllRead,
  onMarkGroupRead,
  onSelect,
  className
}: NotificationsPanelProps & { className?: string }) {
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>({});
  const unreadCount = countUnread(notifications);
  const entries = toEntries(notifications);
  const firstGroupId = entries.flatMap((entry) => (entry.type === "group" ? [entry.group.id] : []))[0];

  const body = loading ? (
    <NotificationsSkeleton label={labels.loading} />
  ) : error ? (
    <Empty className="py-10">
      <EmptyHeader>
        <EmptyMedia variant="icon" className="bg-destructive/10 text-destructive">
          <WarningCircleIcon />
        </EmptyMedia>
        <EmptyTitle>{labels.errorTitle}</EmptyTitle>
        <EmptyDescription>{labels.errorDescription}</EmptyDescription>
      </EmptyHeader>
      {onRetry && (
        <EmptyContent>
          <Button variant="outline" size="sm" shape="pill" onClick={onRetry}>
            <ArrowClockwiseIcon data-icon="inline-start" />
            {labels.retry}
          </Button>
        </EmptyContent>
      )}
    </Empty>
  ) : notifications.length === 0 ? (
    <Empty className="py-10">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <BellSimpleSlashIcon />
        </EmptyMedia>
        <EmptyTitle>{labels.emptyTitle}</EmptyTitle>
        <EmptyDescription>{labels.emptyDescription}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  ) : (
    <ScrollArea className="min-h-0 [&>[data-slot=scroll-area-viewport]]:max-h-[min(26rem,60svh)]">
      <div className="flex flex-col p-1">
        {entries.map((entry) =>
          entry.type === "notification" ? (
            <NotificationRow
              key={entry.notification.id}
              notification={entry.notification}
              unreadLabel={labels.unread}
              onSelect={onSelect}
            />
          ) : (
            <NotificationGroup
              key={entry.group.id}
              group={entry.group}
              notifications={entry.notifications}
              labels={labels}
              open={openGroups[entry.group.id] ?? entry.group.id === firstGroupId}
              onOpenChange={(open) => setOpenGroups((current) => ({ ...current, [entry.group.id]: open }))}
              onMarkRead={onMarkGroupRead}
              onSelect={onSelect}
            />
          )
        )}
      </div>
    </ScrollArea>
  );

  return (
    <div
      data-slot="notifications-panel"
      data-state={loading ? "loading" : error ? "error" : notifications.length === 0 ? "empty" : "ready"}
      aria-busy={loading || undefined}
      className={cn("flex min-h-0 flex-col", className)}
    >
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <h2 className="typo-label-lg">{labels.title}</h2>
        <Button
          variant="ghost"
          size="xs"
          shape="pill"
          disabled={loading || error || unreadCount === 0}
          onClick={onMarkAllRead}
        >
          {labels.markAllRead}
        </Button>
      </div>
      {body}
    </div>
  );
}

function NotificationsMenu({
  notifications,
  labels,
  loading = false,
  error = false,
  onSelect,
  ...panelProps
}: NotificationsPanelProps) {
  const [open, setOpen] = React.useState(false);
  const unreadCount = loading || error ? 0 : countUnread(notifications);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            shape="pill"
            aria-label={labels.trigger(unreadCount)}
            className="relative"
          />
        }
      >
        <BellIcon weight={open ? "fill" : "regular"} className="size-5" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            data-slot="notifications-count"
            className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 typo-label-sm text-primary-foreground tabular-nums ring-2 ring-canvas"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        aria-label={labels.title}
        className="w-[min(22rem,calc(100vw-2rem))] gap-0 overflow-hidden p-0"
      >
        <NotificationsPanel
          notifications={notifications}
          labels={labels}
          loading={loading}
          error={error}
          onSelect={(notification) => {
            onSelect?.(notification);
            setOpen(false);
          }}
          {...panelProps}
        />
      </PopoverContent>
    </Popover>
  );
}

export {
  NotificationsMenu,
  NotificationsPanel,
  type AppNotification,
  type AppNotificationGroup,
  type AppNotificationKind,
  type NotificationsLabels
};
